/** @import { TypedEventTarget } from "~/util/eventtarget.js"; */

import { captureError } from "~/util/sentry.js";
import { keyedThrottle } from "~/util/throttle.js";
/**
 * @import {
 *   InferOutput,
 *   StandardSchemaV1
 * } from "~/util/validate.js"
 */
import {
	any,
	array,
	boolean,
	literal,
	number,
	object,
	optional,
	parse,
	record,
	SchemaError,
	string,
} from "~/util/validate.js";

import Backoff from "./backoff.js";
import * as msg from "./msg.js";

/** @type {StandardSchemaV1<unknown, msg.Candidate>} */
const candidate = object({
	addr: string,
	port: number,
	short_ttl: boolean,
});

const schemata = {
	/** @type {StandardSchemaV1<unknown, msg.ReadyData>} */
	[msg.READY]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.OfferData>} */
	[msg.OFFER]: object({
		ufrag: string,
		pwd: string,
		cert_hash: string,
		candidates: array(candidate),
	}),

	/** @type {StandardSchemaV1<unknown, msg.AnswerData>} */
	[msg.ANSWER]: object({
		ufrag: string,
		pwd: string,
		cert_hash: string,
		candidates: array(candidate),
	}),

	/** @type {StandardSchemaV1<unknown, msg.WithdrawData>} */
	[msg.WITHDRAW]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.DeclineData>} */
	[msg.DECLINE]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.SyncData>} */
	[msg.SYNC]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.CloseData>} */
	[msg.CLOSE]: object({
		code: string,
		message: string,
	}),

	/** @type {StandardSchemaV1<unknown, msg.HostRefreshData>} */
	[msg.HOST_REFRESH]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.AdminMachineRefreshData>} */
	[msg.ADMIN_MACHINE_REFRESH]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.AdminConnectionRefreshData>} */
	[msg.ADMIN_CONNECTION_REFRESH]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.InviteRefreshData>} */
	[msg.INVITE_REFRESH]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.MachineAccessInviteRefreshData>} */
	[msg.MACHINE_ACCESS_INVITE_REFRESH]: object({}),

	/** @type {StandardSchemaV1<unknown, msg.HostUpdateData>} */
	[msg.HOST_UPDATE]: object({
		name: string,
		version: string,
		os: string,
		platform: string,
		source: string,
		channel: string,
	}),
};

/**
 * @template {string} Action
 * @template Data
 * @param {Action} action
 * @param {StandardSchemaV1<unknown, Data>} data
 */
function message(action, data) {
	return object({
		action: literal(action),
		ts: optional(string),
		to: string,
		from: optional(string),
		attempt_id: optional(string),
		metadata: optional(record(any)),
		data: data,
	});
}

const messages = {
	[msg.READY]: message(msg.READY, schemata[msg.READY]),
	[msg.OFFER]: message(msg.OFFER, schemata[msg.OFFER]),
	[msg.ANSWER]: message(msg.ANSWER, schemata[msg.ANSWER]),
	[msg.WITHDRAW]: message(msg.WITHDRAW, schemata[msg.WITHDRAW]),
	[msg.DECLINE]: message(msg.DECLINE, schemata[msg.DECLINE]),
	[msg.SYNC]: message(msg.SYNC, schemata[msg.SYNC]),
	[msg.CLOSE]: message(msg.CLOSE, schemata[msg.CLOSE]),
	[msg.HOST_REFRESH]: message(msg.HOST_REFRESH, schemata[msg.HOST_REFRESH]),
	[msg.ADMIN_MACHINE_REFRESH]: message(
		msg.ADMIN_MACHINE_REFRESH,
		schemata[msg.ADMIN_MACHINE_REFRESH],
	),
	[msg.ADMIN_CONNECTION_REFRESH]: message(
		msg.ADMIN_CONNECTION_REFRESH,
		schemata[msg.ADMIN_CONNECTION_REFRESH],
	),
	[msg.INVITE_REFRESH]: message(msg.INVITE_REFRESH, schemata[msg.INVITE_REFRESH]),
	[msg.MACHINE_ACCESS_INVITE_REFRESH]: message(
		msg.MACHINE_ACCESS_INVITE_REFRESH,
		schemata[msg.MACHINE_ACCESS_INVITE_REFRESH],
	),
	[msg.HOST_UPDATE]: message(msg.HOST_UPDATE, schemata[msg.HOST_UPDATE]),
};

/** @typedef {InferOutput<(typeof messages)[keyof typeof messages]>} Message */

/** @type {Set<string>} */
const REFRESHES = new Set([
	msg.HOST_REFRESH,
	msg.ADMIN_MACHINE_REFRESH,
	msg.ADMIN_CONNECTION_REFRESH,
	msg.INVITE_REFRESH,
	msg.MACHINE_ACCESS_INVITE_REFRESH,
]);

const envelope = object({ action: string });

/**
 * @typedef {object} EventMap
 * @property {{}} connect
 * @property {{}} disconnect
 * @property {Error} error
 * @property {Message} message
 */

export default class WS {
	/** @type {WebSocket | undefined} */
	#ws;

	#events = /** @type {TypedEventTarget<{ [K in keyof EventMap]: CustomEvent<EventMap[K]> }>} */ (
		new EventTarget()
	);

	#reconnect = true;
	#backoff = new Backoff();
	#url = new URL("wss://ws.phaze.app");

	// the backend can send the same refresh in bursts, and each one makes listeners refetch
	#throttleRefresh = keyedThrottle(5_000);

	/**
	 * @template {keyof EventMap} T
	 * @param {T} type
	 * @param {EventMap[T]} detail
	 */
	#send(type, detail) {
		this.#events.dispatchEvent(new CustomEvent(type, { detail }));
	}

	/**
	 * @template {keyof EventMap} T
	 * @param {T} type
	 * @param {(event: CustomEvent<EventMap[T]>) => void} callback
	 * @param {AddEventListenerOptions} [options]
	 */
	on(type, callback, options = {}) {
		this.#events.addEventListener(type, callback, options);
	}

	/**
	 * @param {string} host
	 * @param {string} token
	 * @param {string[]} subscriptions
	 */
	connect(host, token, subscriptions) {
		this.#url = new URL(host.includes("://") ? host : `wss://${host}`);
		this.#url.searchParams.set("token", token);
		this.#url.searchParams.set("subscriptions", subscriptions.join(","));
		this.#connect();
	}

	async #connect() {
		this.#ws = new WebSocket(this.#url);
		this.#ws.onopen = () => {
			this.#backoff.stop();
			this.#backoff = new Backoff();

			this.#send("connect", {});
		};

		this.#ws.onmessage = evt => {
			try {
				const data = JSON.parse(evt.data);
				const { action } = parse(envelope, data);
				/** @type {StandardSchemaV1<unknown, Message> | undefined} */
				const schema = messages[/** @type {keyof typeof messages} */ (action)];
				if (!schema) throw new SchemaError([{ message: `Unknown action "${action}"` }]);
				const message = parse(schema, data);
				if (REFRESHES.has(action))
					this.#throttleRefresh(action, () => this.#send("message", message));
				else this.#send("message", message);
			} catch (e) {
				if (e instanceof SchemaError) captureError(e);
				this.#send("error", /** @type {Error} */ (e));
			}
		};

		this.#ws.onerror = () => {};

		this.#ws.onclose = async () => {
			if (!this.#reconnect) {
				this.#send("disconnect", {});
				return;
			}

			try {
				await this.#backoff.increment();
				this.#connect();
			} catch {
				// backoff was aborted
			}
		};
	}

	/** @param {boolean} reconnect */
	disconnect(reconnect) {
		this.#reconnect = reconnect;
		if (!reconnect) this.#backoff.stop();

		this.#ws?.close();
	}
}
